import React, { useState } from 'react';
import { Link, router } from '@inertiajs/react';
import Layout from '../../../components/Layout.jsx';

export default function SuperadminNabDomainsIndex({ nab_domains = [], filters = {} }) {
  const [query, setQuery] = useState(filters.q || '');
  const [pendingId, setPendingId] = useState(null);

  const handleSearch = (e) => {
    e.preventDefault();
    router.get('/superadmin/nab_domains', { q: query }, { preserveState: true, preserveScroll: true });
  };

  const handleClear = () => {
    setQuery('');
    router.get('/superadmin/nab_domains', {}, { preserveState: true, preserveScroll: true });
  };

  const handleDelete = (domain) => {
    const ok = window.confirm(`Delete NAB domain "${domain.code} — ${domain.name}"?`);
    if (!ok) return;
    setPendingId(domain.id);
    router.delete(`/superadmin/nab_domains/${domain.id}`, {
      onFinish: () => setPendingId(null),
    });
  };

  const total = nab_domains.length;
  const hasFilter = (filters.q || '').trim().length > 0;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6 sm:p-8 space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
          <div>
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 mb-2 rounded-full bg-purple-50 text-purple-700 border border-purple-200 text-xs font-medium">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
              Superadmin
            </div>
            <h1 className="text-3xl font-bold tracking-tight text-gray-900">NAB Domains</h1>
            <p className="mt-1 text-sm text-gray-500">Reference list of NAB competency domains used to tag training content.</p>
          </div>
          <Link
            href="/superadmin/nab_domains/new"
            className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm transition"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M12 4v16m8-8H4" />
            </svg>
            Add domain
          </Link>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
                </svg>
              </span>
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search by code or name…"
                className="w-full pl-10 pr-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm transition cursor-pointer"
              >
                Search
              </button>
              {hasFilter && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="inline-flex items-center justify-center px-4 py-2.5 text-sm font-semibold rounded-lg text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 transition cursor-pointer"
                >
                  Clear
                </button>
              )}
            </div>
          </form>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
          <div className="flex items-center gap-2 mb-4">
            <div className="w-8 h-8 bg-indigo-50 text-indigo-600 rounded-lg flex items-center justify-center">
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-gray-900">
              {hasFilter ? 'Matching domains' : 'All domains'}
            </h2>
            <span className="ml-auto text-sm text-gray-400">
              {total} {total === 1 ? 'domain' : 'domains'}
            </span>
          </div>

          {total === 0 ? (
            <div className="text-center py-12">
              <div className="mx-auto w-12 h-12 bg-gray-50 text-gray-400 rounded-2xl flex items-center justify-center mb-3">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              </div>
              <p className="text-gray-600">
                {hasFilter ? 'No domains match that search.' : 'No NAB domains yet.'}
              </p>
              {hasFilter ? (
                <button
                  onClick={handleClear}
                  className="mt-3 text-sm font-semibold text-indigo-600 hover:text-indigo-700 cursor-pointer"
                >
                  Clear search
                </button>
              ) : (
                <Link
                  href="/superadmin/nab_domains/new"
                  className="mt-3 inline-flex text-sm font-semibold text-indigo-600 hover:text-indigo-700"
                >
                  Add the first one →
                </Link>
              )}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {nab_domains.map((domain) => (
                <div key={domain.id} className="flex flex-col sm:flex-row sm:items-center sm:justify-between py-3 gap-3 first:pt-0 last:pb-0">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="shrink-0 inline-flex items-center justify-center min-w-[3.5rem] px-2.5 py-1 rounded-md bg-indigo-50 text-indigo-700 border border-indigo-200 text-xs font-bold font-mono">
                      {domain.code}
                    </span>
                    <p className="font-medium text-gray-900 truncate">{domain.name}</p>
                  </div>
                  <div className="flex gap-2 shrink-0">
                    <Link
                      href={`/superadmin/nab_domains/${domain.id}/edit`}
                      className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 transition"
                    >
                      Edit
                    </Link>
                    <button
                      type="button"
                      onClick={() => handleDelete(domain)}
                      disabled={pendingId === domain.id}
                      className="inline-flex items-center justify-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg text-red-700 bg-white hover:bg-red-50 border border-red-200 transition cursor-pointer disabled:opacity-50"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
